import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  FileText,
  Eye,
  EyeOff,
  Search,
  Star,
  Calendar,
  ExternalLink,
  Save,
  X,
  Bold,
  Italic,
  List,
  Link,
  Image,
  Heading1,
  Heading2,
  Pilcrow,
  WrapText
} from 'lucide-react';

const AdminBlogPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    cover_image: '',
    author: 'Quero Publicar',
    category: 'Publicação Científica',
    tags: [],
    published: false,
    featured: false
  });

  const [tagInput, setTagInput] = useState('');
  const [editorRef, setEditorRef] = useState(null);

  const categories = [
    'Publicação Científica',
    'Metodologia',
    'QUALIS/CAPES',
    'Dicas de Escrita',
    'Eventos Acadêmicos',
    'Novidades',
    'Tutoriais',
    'Geral'
  ];

  // Convert plain text to HTML paragraphs
  const convertToHtmlParagraphs = (text) => {
    if (!text) return '';
    
    // Split by double line breaks (paragraphs) or single line breaks
    const paragraphs = text
      .split(/\n\s*\n|\n/)  // Split by double or single newlines
      .map(p => p.trim())
      .filter(p => p.length > 0);
    
    // Wrap each paragraph in <p> tags
    return paragraphs.map(p => `<p>${p}</p>`).join('\n\n');
  };

  // Handle paste event to preserve paragraphs
  const handlePaste = (e) => {
    e.preventDefault();
    
    const pastedText = e.clipboardData.getData('text/plain');
    
    if (pastedText) {
      const htmlContent = convertToHtmlParagraphs(pastedText);
      
      if (editorRef) {
        const start = editorRef.selectionStart;
        const end = editorRef.selectionEnd;
        const currentContent = formData.content;
        const before = currentContent.substring(0, start);
        const after = currentContent.substring(end);
        
        const newContent = before + htmlContent + after;
        setFormData(prev => ({ ...prev, content: newContent }));
        
        // Set cursor position after pasted content
        setTimeout(() => {
          if (editorRef) {
            const newPosition = start + htmlContent.length;
            editorRef.selectionStart = newPosition;
            editorRef.selectionEnd = newPosition;
            editorRef.focus();
          }
        }, 0);
      }
    }
  };

  // Convert entire content to paragraphs
  const formatAllParagraphs = () => {
    // Remove existing HTML tags first, then re-format
    const plainText = formData.content
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<p>/gi, '')
      .replace(/<\/p>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '') // Remove any remaining tags
      .trim();
    
    const htmlContent = convertToHtmlParagraphs(plainText);
    setFormData(prev => ({ ...prev, content: htmlContent }));
  };

  // Simple formatting helpers
  const insertFormat = (tag, endTag = null) => {
    if (!editorRef) return;
    const start = editorRef.selectionStart;
    const end = editorRef.selectionEnd;
    const text = formData.content;
    const selectedText = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    const openTag = `<${tag}>`;
    const closeTag = endTag || `</${tag}>`;
    const newContent = before + openTag + selectedText + closeTag + after;
    
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  // Insert paragraph break
  const insertParagraph = () => {
    insertAtCursor('</p>\n\n<p>');
  };

  const insertLink = () => {
    const url = prompt('Digite a URL do link:');
    if (url) {
      const text = prompt('Texto do link (deixe vazio para usar a URL):') || url;
      insertAtCursor(`<a href="${url}" target="_blank">${text}</a>`);
    }
  };

  const insertImage = () => {
    const url = prompt('Digite a URL da imagem:');
    if (url) {
      insertAtCursor(`<img src="${url}" alt="Imagem" style="max-width: 100%; height: auto;" />`);
    }
  };

  const insertAtCursor = (text) => {
    if (!editorRef) return;
    const start = editorRef.selectionStart;
    const before = formData.content.substring(0, start);
    const after = formData.content.substring(start);
    setFormData(prev => ({ ...prev, content: before + text + after }));
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPosts();
  }, [user, navigate]);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/api/blog/admin/all?limit=100');
      setPosts(response.data.posts);
    } catch (error) {
      toast.error('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    
    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      toast.error('Conteúdo é obrigatório');
      return;
    }
    
    setSaving(true);
    
    try {
      if (editingPost) {
        await api.put(`/api/blog/${editingPost.post_id}`, formData);
        toast.success('Post atualizado!');
      } else {
        await api.post('/api/blog', formData);
        toast.success('Post criado!');
      }
      
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar post');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      cover_image: post.cover_image || '',
      author: post.author || 'Quero Publicar',
      category: post.category || 'Geral',
      tags: post.tags || [],
      published: post.published || false,
      featured: post.featured || false
    });
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Tem certeza que deseja excluir este post?')) return;
    
    try {
      await api.delete(`/api/blog/${postId}`);
      toast.success('Post excluído!');
      fetchPosts();
    } catch (error) {
      toast.error('Erro ao excluir post');
    }
  };

  const togglePublished = async (post) => {
    try {
      await api.put(`/api/blog/${post.post_id}`, { published: !post.published });
      toast.success(post.published ? 'Post despublicado' : 'Post publicado!');
      fetchPosts();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const toggleFeatured = async (post) => {
    try {
      await api.put(`/api/blog/${post.post_id}`, { featured: !post.featured });
      toast.success(post.featured ? 'Destaque removido' : 'Post destacado!');
      fetchPosts();
    } catch (error) {
      toast.error('Erro ao atualizar destaque');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      cover_image: '',
      author: 'Quero Publicar',
      category: 'Publicação Científica',
      tags: [],
      published: false,
      featured: false
    });
    setTagInput('');
    setEditingPost(null);
    setShowEditor(false);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-[#002FA7]" />
                <h1 className="text-xl font-bold">Blog</h1>
              </div>
            </div>
            <Button
              onClick={() => { resetForm(); setShowEditor(true); }}
              className="bg-[#002FA7] hover:bg-[#001f75] gap-2"
            >
              <Plus className="h-4 w-4" />
              Novo Post
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Editor Modal */}
        {showEditor && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-auto py-8">
            <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold">
                  {editingPost ? 'Editar Post' : 'Novo Post'}
                </h2>
                <Button variant="ghost" size="icon" onClick={resetForm}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-1">Título *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Como publicar em revistas QUALIS A1"
                    className="text-lg"
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <label className="block text-sm font-medium mb-1">Resumo (para listagem)</label>
                  <Input
                    value={formData.excerpt}
                    onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                    placeholder="Breve descrição do post para aparecer nas listagens..."
                  />
                </div>

                {/* Cover Image */}
                <div>
                  <label className="block text-sm font-medium mb-1">URL da Imagem de Capa</label>
                  <Input
                    value={formData.cover_image}
                    onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))}
                    placeholder="https://..."
                  />
                  {formData.cover_image && (
                    <img 
                      src={formData.cover_image} 
                      alt="Preview" 
                      className="mt-2 h-32 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>

                {/* Category and Author */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Categoria</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Autor</label>
                    <Input
                      value={formData.author}
                      onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium mb-1">Tags</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Adicionar tag..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>
                      Adicionar
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="cursor-pointer hover:bg-red-100"
                        onClick={() => removeTag(tag)}
                      >
                        {tag} ✕
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Content Editor */}
                <div>
                  <label className="block text-sm font-medium mb-1">Conteúdo * (suporta HTML)</label>
                  
                  {/* Toolbar */}
                  <div className="flex flex-wrap gap-1 p-2 bg-gray-100 border border-gray-300 border-b-0 rounded-t-lg">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => insertFormat('h2')}
                      title="Título"
                    >
                      <Heading1 className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => insertFormat('h3')}
                      title="Subtítulo"
                    >
                      <Heading2 className="h-4 w-4" />
                    </Button>
                    <div className="w-px h-6 bg-gray-300 mx-1" />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => insertFormat('strong')}
                      title="Negrito"
                    >
                      <Bold className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => insertFormat('em')}
                      title="Itálico"
                    >
                      <Italic className="h-4 w-4" />
                    </Button>
                    <div className="w-px h-6 bg-gray-300 mx-1" />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => insertFormat('ul')}
                      title="Lista"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={insertLink}
                      title="Link"
                    >
                      <Link className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={insertImage}
                      title="Imagem"
                    >
                      <Image className="h-4 w-4" />
                    </Button>
                    <div className="w-px h-6 bg-gray-300 mx-1" />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={insertParagraph}
                      title="Novo Parágrafo"
                    >
                      <Pilcrow className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={formatAllParagraphs}
                      title="Formatar todos os parágrafos"
                      className="ml-2 text-xs bg-blue-50 hover:bg-blue-100 border-blue-200"
                    >
                      <WrapText className="h-4 w-4 mr-1" />
                      Auto-formatar
                    </Button>
                  </div>
                  
                  {/* Help text */}
                  <div className="bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800 rounded-none">
                    💡 <strong>Dica:</strong> Ao colar texto, os parágrafos são formatados automaticamente. 
                    Ou clique em "Auto-formatar" para converter texto simples em parágrafos HTML.
                  </div>
                  
                  <textarea
                    ref={setEditorRef}
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    onPaste={handlePaste}
                    placeholder="Cole ou escreva o conteúdo do post aqui...&#10;&#10;Os parágrafos serão formatados automaticamente ao colar."
                    className="w-full min-h-[350px] p-4 border border-gray-300 border-t-0 rounded-b-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                  />
                  
                  {/* Preview */}
                  {formData.content && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium mb-2">Preview:</label>
                      <div 
                        className="p-4 border border-gray-200 rounded-lg bg-white prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: formData.content }}
                      />
                    </div>
                  )}
                </div>

                {/* Options */}
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.published}
                      onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-[#002FA7] focus:ring-[#002FA7]"
                    />
                    <span className="text-sm font-medium">Publicar imediatamente</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                    />
                    <span className="text-sm font-medium">Destacar post</span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button 
                    type="submit" 
                    className="bg-[#002FA7] hover:bg-[#001f75] gap-2"
                    disabled={saving}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Salvando...' : (editingPost ? 'Salvar Alterações' : 'Criar Post')}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por título ou categoria..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Posts List */}
        <div className="bg-white border border-[#E4E4E7] rounded-lg">
          <div className="p-4 border-b border-[#E4E4E7]">
            <p className="text-sm text-gray-600">
              {filteredPosts.length} post(s)
            </p>
          </div>
          
          {filteredPosts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum post encontrado</p>
              <Button
                onClick={() => setShowEditor(true)}
                className="mt-4 bg-[#002FA7] hover:bg-[#001f75]"
              >
                Criar primeiro post
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#E4E4E7]">
              {filteredPosts.map((post) => (
                <div key={post.post_id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-4">
                    {/* Cover */}
                    {post.cover_image && (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-24 h-16 object-cover rounded flex-shrink-0"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold line-clamp-1">{post.title}</h3>
                          <p className="text-sm text-gray-500 line-clamp-1">{post.excerpt || 'Sem resumo'}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {post.featured && (
                            <Badge className="bg-yellow-100 text-yellow-700">Destaque</Badge>
                          )}
                          {post.published ? (
                            <Badge className="bg-green-100 text-green-700">Publicado</Badge>
                          ) : (
                            <Badge variant="secondary">Rascunho</Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                        <span>{post.category}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {post.views || 0} views
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleFeatured(post)}
                        title={post.featured ? 'Remover destaque' : 'Destacar'}
                      >
                        <Star className={`h-4 w-4 ${post.featured ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePublished(post)}
                        title={post.published ? 'Despublicar' : 'Publicar'}
                      >
                        {post.published ? (
                          <Eye className="h-4 w-4 text-green-600" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                        title="Ver post"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(post)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(post.post_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminBlogPage;
